import { z } from 'zod';

/**
 * Outcome of a leads-api call availability check (MP-4244).
 *
 * - `available`: a Boberdoo buyer matched the ping.
 * - `no_buyers`: the ping ran and nobody matched (outside hours, capped, filtered).
 * - `timeout`: the lead lookup or the ping exceeded its leads-api timeout.
 * - `error`: the ping or lead-storage failed.
 * - `no_lead`: no lead exists for the session, so no ping was made.
 * - `unmapped_ring_pool`: leads-api has no Boberdoo filterset for the ring pool.
 */
export const CallAvailabilityStatus = z.enum([
  'available',
  'no_buyers',
  'timeout',
  'error',
  'no_lead',
  'unmapped_ring_pool',
]);

// eslint-disable-next-line no-redeclare
export type CallAvailabilityStatus = z.infer<typeof CallAvailabilityStatus>;

/**
 * Body of `POST /api/v1/call/campaign/availability`.
 *
 * `ring_pool_id` must be the numeric Invoca Ring Pool ID only; leads-api
 * rejects anything else with a 400 so the `ring_pool_key` is never sent.
 */
export const FetchCallAvailabilityPayloadDTO = z.object({
  domain_session_id: z.string().min(1),
  domain_user_id: z.string().optional(),
  ring_pool_id: z.string().regex(/^\d+$/),
  product: z.string().min(1),
  placement: z.string().optional(),
  smid: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});

// eslint-disable-next-line no-redeclare
export type FetchCallAvailabilityPayloadDTO = z.infer<
  typeof FetchCallAvailabilityPayloadDTO
>;

/**
 * Response of `POST /api/v1/call/campaign/availability`. Never carries buyer,
 * DID/SRC or PII data.
 */
export const FetchCallAvailabilityResponseDTO = z.object({
  status: CallAvailabilityStatus,
});

// eslint-disable-next-line no-redeclare
export type FetchCallAvailabilityResponseDTO = z.infer<
  typeof FetchCallAvailabilityResponseDTO
>;
